import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, switchMap } from 'rxjs';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { LogicalWarehouseService } from '../services/logical-warehouse.service';
import {
  LogicalWarehouse,
  LogicalWarehouseSearchParams,
  LogicalWarehouseUpdateRequest,
} from '../models/logical-warehouse.model';
import {
  VietnamAddressService,
  VietnamProvince,
  VietnamWard,
} from '../../services/vietnam-address.service';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { PermissionService } from '@app/core/services/permission.service';
import {
  LOGICAL_WAREHOUSE_SEARCH_CONFIG,
  LOGICAL_WAREHOUSE_COLUMNS,
  LOGICAL_WAREHOUSE_DEFAULT_COLUMNS_ORDER,
  LOGICAL_WAREHOUSE_FLEXIBLE_COLUMNS_ORDER,
  LOGICAL_WAREHOUSE_ACTION_COLUMN,
} from '../logical-warehouse.config';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import {
  buildWarehouseDefaultColumns,
  buildWarehouseFlexibleColumns,
} from '../../utils/warehouse-table-columns.util';
import { isWarehouseAssociatedResponse } from '../../utils/warehouse-association.util';
import {
  buildWarehouseDeleteDialogState,
  buildWarehouseStatusChangeDialogState,
} from '../../utils/warehouse-confirmation-dialog.util';
import {
  WAREHOUSE_LIST_STATUS_TAG_STYLE,
  warehouseListStatusDisplayText,
  warehouseListStatusSeverity,
} from '../../utils/warehouse-status-tag.util';
import { openWarehouseTableColumnSelector } from '../../utils/warehouse-column-selector.util';
import {
  logicalWarehouseListHasActiveFilters,
  mergeLogicalWarehouseListSearch,
  resetLogicalWarehouseListSearch,
} from '../../utils/warehouse-logical-list-search.util';
import { resolveLogicalWarehouseDeleteErrorKey } from '../logical-warehouse-delete-error.util';

@Component({
  selector: 'app-logical-warehouse-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './logical-warehouse-list.component.html',
  styleUrl: './logical-warehouse-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogicalWarehouseListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly logicalWarehouseService = inject(LogicalWarehouseService);
  private readonly translateService = inject(TranslateService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly vietnamAddressService = inject(VietnamAddressService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'logical_warehouse' for warehouse permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.logical_warehouse.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.logical_warehouse.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.logical_warehouse.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.logical_warehouse.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.logical_warehouse.approve');
  });

  // Search configuration
  searchConfig = LOGICAL_WAREHOUSE_SEARCH_CONFIG;

  // Check if user has any action permissions (UPDATE, APPROVE, or DELETE)
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canApprove() || this.canDelete(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] =>
    buildWarehouseDefaultColumns(
      LOGICAL_WAREHOUSE_COLUMNS,
      LOGICAL_WAREHOUSE_DEFAULT_COLUMNS_ORDER,
      LOGICAL_WAREHOUSE_ACTION_COLUMN.ACTIONS,
      this.hasAnyActionPermission(),
    ),
  );

  flexibleColumns = computed((): TableColumn[] =>
    buildWarehouseFlexibleColumns(
      LOGICAL_WAREHOUSE_COLUMNS,
      LOGICAL_WAREHOUSE_FLEXIBLE_COLUMNS_ORDER,
    ),
  );

  // Data signals
  warehouses = signal<LogicalWarehouse[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  provinces = signal<VietnamProvince[]>([]);
  wardsByProvince = signal<Record<number, VietnamWard[]>>({});

  // Search parameters
  searchParams = signal<LogicalWarehouseSearchParams>({
    name: undefined,
    code: undefined,
    typeCodes: undefined,
    page: 0,
    size: 20,
  });

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedWarehouseForAction: LogicalWarehouse | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | 'statusChange' | null = null;

  // Computed values - map warehouses with translated addresses
  warehousesData = computed(() => {
    return this.warehouses().map((warehouse) => ({
      ...warehouse,
      translatedAddress: this.getFullAddress(warehouse),
    }));
  });

  ngOnInit(): void {
    this.loadProvinces();
    this.loadWarehouses();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Primary button configuration - computed with permissions
  primaryButton = computed(() => ({
    label: this.translateService.instant('common.actions.new'),
    severity: 'info' as const,
    outlined: false,
    show: this.canCreate(),
    icon: 'add',
  }));

  onSearch(params: {
    name?: string;
    code?: string;
    typeCodes?: string[];
  }): void {
    this.searchParams.set(
      mergeLogicalWarehouseListSearch(this.searchParams(), params),
    );
    this.loadWarehouses();
  }

  onReset(): void {
    const currentParams = this.searchParams();
    if (!logicalWarehouseListHasActiveFilters(currentParams)) {
      return;
    }
    this.searchParams.set({
      ...this.searchParams(),
      ...resetLogicalWarehouseListSearch(20),
    });
    this.loadWarehouses();
  }

  onAddWarehouse(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/warehouse/logical/add']);
  }

  onEdit(warehouse: LogicalWarehouse): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/warehouse/logical/edit', warehouse.id]);
  }

  onStatusChange(warehouse: LogicalWarehouse): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    const newStatus = !warehouse.status;
    this.selectedWarehouseForAction = warehouse;
    const dlg = buildWarehouseStatusChangeDialogState(
      (k, p) => this.translateService.instant(k, p),
      'logical_warehouse',
      warehouse.name,
      newStatus,
      false,
    );
    this.dialogTitle = dlg.title;
    this.dialogMessage = dlg.message;
    this.dialogButtons = dlg.buttons;
    this.currentAction = 'statusChange';
    this.showConfirmationDialog = true;
  }

  onDelete(warehouse: LogicalWarehouse): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedWarehouseForAction = warehouse;
    const dlg = buildWarehouseDeleteDialogState(
      (k, p) => this.translateService.instant(k, p),
      'logical_warehouse',
      warehouse.name,
    );
    this.dialogTitle = dlg.title;
    this.dialogMessage = dlg.message;
    this.dialogButtons = dlg.buttons;
    this.currentAction = 'delete';
    this.showConfirmationDialog = true;
  }

  onConfirmationButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction === 'delete') {
        this.confirmDelete();
      } else if (this.currentAction === 'statusChange') {
        this.confirmStatusChange();
      }
    }
  }

  confirmDelete(): void {
    if (this.selectedWarehouseForAction) {
      this.deleteWarehouse();
    }
    this.closeConfirmationDialog();
  }

  confirmStatusChange(): void {
    if (this.selectedWarehouseForAction) {
      this.changeWarehouseStatus();
    }
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedWarehouseForAction = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  private changeWarehouseStatus(): void {
    if (!this.selectedWarehouseForAction?.id) {
      return;
    }

    const warehouseId = this.selectedWarehouseForAction.id;
    const newStatus = !this.selectedWarehouseForAction.status;

    if (newStatus) {
      // Activating - no need to check association
      this.performStatusChange(warehouseId, newStatus);
    } else {
      // If deactivating, check if warehouse is associated with stores
      this.logicalWarehouseService
        .checkWarehouseAssociation([warehouseId])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (isAssociated: unknown) => {
            if (isWarehouseAssociatedResponse(isAssociated)) {
              this.messageService.add({
                severity: 'warn',
                summary: this.translateService.instant(
                  'logical_warehouse.messages.warning.title',
                ),
                detail: this.translateService.instant(
                  'logical_warehouse.messages.warning.warehouse_in_use',
                ),
                life: 6000,
              });
              return;
            }

            // Proceed with deactivation if not associated
            this.performStatusChange(warehouseId, newStatus);
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant(
                'logical_warehouse.messages.error.title',
              ),
              detail: this.translateService.instant(
                'logical_warehouse.messages.error.failed_to_update_status',
              ),
              life: 6000,
            });
          },
        });
    }
  }

  private performStatusChange(warehouseId: number, newStatus: boolean): void {
    // First, fetch full warehouse data from API to ensure we have all required fields
    this.logicalWarehouseService
      .getLogicalWarehouseById(warehouseId)
      .pipe(
        switchMap((fullWarehouse) => {
          // Create update data with full warehouse information
          const updateData: LogicalWarehouseUpdateRequest = {
            ...fullWarehouse,
            id: warehouseId,
            status: newStatus,
          };

          // Update warehouse with full data
          return this.logicalWarehouseService.updateLogicalWarehouse(
            updateData,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'logical_warehouse.messages.success.title',
            ),
            detail: this.translateService.instant(
              `logical_warehouse.messages.success.warehouse_${
                newStatus ? 'activated' : 'deactivated'
              }`,
            ),
            life: 4000,
          });
          this.loadWarehouses();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'logical_warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'logical_warehouse.messages.error.failed_to_update_status',
            ),
            life: 6000,
          });
        },
      });
  }

  private deleteWarehouse(): void {
    if (!this.selectedWarehouseForAction?.id) {
      return;
    }

    const warehouseId = this.selectedWarehouseForAction.id;

    // Check if warehouse is associated with any stores
    this.logicalWarehouseService
      .checkWarehouseAssociation([warehouseId])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (isAssociated: unknown) => {
          if (isWarehouseAssociatedResponse(isAssociated)) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translateService.instant(
                'logical_warehouse.messages.warning.title',
              ),
              detail: this.translateService.instant(
                'logical_warehouse.messages.warning.warehouse_in_use',
              ),
              life: 6000,
            });
            return;
          }

          // Proceed with delete if not associated
          this.logicalWarehouseService
            .deleteLogicalWarehouses([warehouseId])
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: this.translateService.instant(
                    'logical_warehouse.messages.success.title',
                  ),
                  detail: this.translateService.instant(
                    'logical_warehouse.messages.success.warehouse_deleted',
                  ),
                  life: 4000,
                });
                this.loadWarehouses();
              },
              error: (error) => {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translateService.instant(
                    'logical_warehouse.messages.error.title',
                  ),
                  detail: this.translateService.instant(
                    resolveLogicalWarehouseDeleteErrorKey(error),
                  ),
                  life: 6000,
                });
              },
            });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'logical_warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'logical_warehouse.messages.error.failed_to_check_association',
            ),
            life: 6000,
          });
        },
      });
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page, // Advanced table uses 0-based page
      size: event.rows,
    });
    this.loadWarehouses();
  }

  getTypeCodeDisplayText(typeCode: string): string {
    if (!typeCode) return '';
    const key = `logical_warehouse.form.type_codes.${typeCode.toLowerCase()}`;
    return this.translateService.instant(key);
  }

  private loadWarehouses(): void {
    this.loading.set(true);

    this.logicalWarehouseService
      .getLogicalWarehouses(this.searchParams())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.warehouses.set(response.content || []);
          this.totalRecords.set(response.totalElements || 0);

          const provinceIds = this.extractProvinceIds(response.content || []);
          this.loadWardsForProvinces(provinceIds);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'logical_warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'logical_warehouse.messages.error.failed_to_load',
            ),
          });
          this.loading.set(false);
        },
      });
  }

  private extractProvinceIds(warehouses: LogicalWarehouse[]): number[] {
    return Array.from(
      new Set(
        warehouses
          .map((w) => this.resolveProvinceId(w))
          .filter((id): id is number => id !== null && id > 0),
      ),
    );
  }

  private resolveProvinceId(warehouse: LogicalWarehouse): number | null {
    if (warehouse.provinceCode && warehouse.provinceCode > 0) {
      return warehouse.provinceCode;
    }

    if (!warehouse.cityName) {
      return null;
    }

    const provinces = this.provinces();
    const province = provinces.find(
      (p) =>
        p.full_name_en === warehouse.cityName ||
        p.name_en === warehouse.cityName ||
        p.full_name === warehouse.cityName ||
        p.name === warehouse.cityName ||
        (p.full_name_en && warehouse.cityName.includes(p.full_name_en)) ||
        (p.name_en && warehouse.cityName.includes(p.name_en)),
    );

    if (province) {
      return typeof province.id === 'string'
        ? Number.parseInt(province.id, 10)
        : province.id;
    }

    const cityNameLower = warehouse.cityName.toLowerCase();
    if (cityNameLower.includes('ha noi') || cityNameLower.includes('hanoi')) {
      return 1;
    }

    return null;
  }

  private loadWardsForProvinces(provinceIds: number[]): void {
    if (provinceIds.length === 0) {
      this.loading.set(false);
      return;
    }

    const loadPromises = provinceIds.map((provinceId) => {
      return new Promise<void>((resolve) => {
        if (this.wardsByProvince()[provinceId]?.length > 0) {
          resolve();
          return;
        }

        this.vietnamAddressService
          .loadWardsByProvince(provinceId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (wards) => {
              if (wards?.length > 0) {
                const current = this.wardsByProvince();
                this.wardsByProvince.set({
                  ...current,
                  [provinceId]: wards,
                });
              }
              resolve();
            },
            error: (error) => {
              console.error(
                'Error loading wards for province',
                provinceId,
                error,
              );
              resolve();
            },
          });
      });
    });

    Promise.all(loadPromises).then(() => {
      this.loading.set(false);
    });
  }

  getStatusIcon(status: boolean): string {
    return status ? 'pi pi-check-circle' : 'pi pi-times-circle';
  }

  getStatusDisplayText(status: boolean): string {
    return warehouseListStatusDisplayText(
      (k) => this.translateService.instant(k),
      'logical_warehouse',
      status,
    );
  }

  getStatusSeverity(status: boolean): string {
    return warehouseListStatusSeverity(status);
  }

  getStatusStyle(_status: boolean): Record<string, string> {
    return { ...WAREHOUSE_LIST_STATUS_TAG_STYLE };
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    openWarehouseTableColumnSelector(this.advancedTable, event);
  }

  getFullAddress(warehouse: LogicalWarehouse): string {
    if (!warehouse) return '';

    const provinceCode = this.resolveProvinceCode(warehouse);
    const province = this.getTranslatedProvince(
      warehouse.provinceName,
      warehouse.provinceCode || provinceCode,
    );
    const city = this.getTranslatedProvince(
      warehouse.cityName,
      warehouse.cityCode,
    );
    const district = this.getTranslatedDistrict(
      warehouse.districtName,
      provinceCode || warehouse.provinceCode,
      warehouse.districtCode,
    );

    const parts: string[] = [];

    if (warehouse.detailAddress?.trim()) {
      parts.push(warehouse.detailAddress.trim());
    }
    if (district?.trim()) {
      parts.push(district.trim());
    }
    if (city?.trim()) {
      parts.push(city.trim());
    }
    if (province?.trim()) {
      parts.push(province.trim());
    }

    const country = warehouse.countryName?.trim() || 'Việt Nam';
    if (country) {
      parts.push(country);
    }

    return parts.join(', ');
  }

  private resolveProvinceCode(warehouse: LogicalWarehouse): number | undefined {
    if (warehouse.provinceCode) {
      return warehouse.provinceCode;
    }

    if (!warehouse.cityName) {
      return undefined;
    }

    const provinces = this.provinces();
    const province = provinces.find(
      (p) =>
        p.full_name_en === warehouse.cityName ||
        p.name_en === warehouse.cityName ||
        p.full_name === warehouse.cityName ||
        p.name === warehouse.cityName ||
        (p.full_name_en && warehouse.cityName.includes(p.full_name_en)) ||
        (p.name_en && warehouse.cityName.includes(p.name_en)),
    );

    if (province) {
      return typeof province.id === 'string'
        ? Number.parseInt(province.id)
        : province.id;
    }

    const cityNameLower = warehouse.cityName.toLowerCase();
    if (cityNameLower.includes('ha noi') || cityNameLower.includes('hanoi')) {
      return 1;
    }

    return undefined;
  }

  private loadProvinces(): void {
    this.vietnamAddressService.loadProvinces().subscribe({
      next: (provinces) => {
        this.provinces.set(provinces || []);
      },
      error: (error) => {
        console.error('Error loading provinces:', error);
        this.provinces.set([]);
      },
    });
  }

  private getTranslatedProvince(name?: string, code?: number): string {
    if (!name) return '';

    const currentLang = this.translateService.getCurrentLang() || 'vi';
    if (currentLang !== 'vi') return name;

    const provinces = this.provinces();
    if (!provinces?.length) return name;

    let province: VietnamProvince | undefined;
    if (code) {
      province = provinces.find((p) => p.id === code);
    }
    if (!province) {
      province = provinces.find(
        (p) =>
          p.full_name_en === name ||
          p.name_en === name ||
          p.full_name === name ||
          p.name === name,
      );
    }

    return province?.full_name || name;
  }

  private getTranslatedDistrict(
    name?: string,
    provinceCode?: number,
    districtCode?: number,
  ): string {
    if (!name) return '';

    const currentLang = this.translateService.currentLang || 'vi';
    const finalProvinceCode = provinceCode || this.getDefaultProvinceCode();

    if (!finalProvinceCode) {
      return name;
    }

    const wards = this.wardsByProvince()[finalProvinceCode];
    if (!wards?.length) {
      return name;
    }

    const normalizedName = name
      .replace(/\s+Ward\s*$/i, '')
      .replace(/\s+Commune\s*$/i, '')
      .trim();

    const ward = this.findWard(wards, name, normalizedName, districtCode);

    if (ward) {
      return currentLang === 'vi'
        ? ward.full_name
        : ward.full_name_en || ward.full_name;
    }

    return name;
  }

  private getDefaultProvinceCode(): number | null {
    const provinces = this.provinces();
    return provinces?.length > 0 ? 1 : null;
  }

  private findWard(
    wards: VietnamWard[],
    originalName: string,
    normalizedName: string,
    districtCode?: number,
  ): VietnamWard | undefined {
    // Strategy 0: Match by districtCode (most accurate)
    if (districtCode) {
      const ward = wards.find((w) => w.id === districtCode);
      if (ward) return ward;
    }

    // Strategy 1: Exact match with original name
    let ward = wards.find(
      (w) =>
        w.full_name_en === originalName ||
        w.name_en === originalName ||
        w.full_name === originalName ||
        w.name === originalName,
    );
    if (ward) return ward;

    // Strategy 2: Exact match with normalized name
    ward = wards.find(
      (w) =>
        w.full_name_en === normalizedName ||
        w.name_en === normalizedName ||
        w.full_name === normalizedName ||
        w.name === normalizedName,
    );
    if (ward) return ward;

    // Strategy 3: Case-insensitive exact match
    const lowerNormalizedName = normalizedName.toLowerCase();
    const lowerOriginalName = originalName.toLowerCase();

    ward = wards.find(
      (w) =>
        w.full_name_en?.toLowerCase() === lowerNormalizedName ||
        w.name_en?.toLowerCase() === lowerNormalizedName ||
        w.full_name?.toLowerCase() === lowerNormalizedName ||
        w.name?.toLowerCase() === lowerNormalizedName ||
        w.full_name_en?.toLowerCase() === lowerOriginalName ||
        w.name_en?.toLowerCase() === lowerOriginalName ||
        w.full_name?.toLowerCase() === lowerOriginalName ||
        w.name?.toLowerCase() === lowerOriginalName,
    );
    if (ward) return ward;

    // Strategy 4: Partial matching
    return wards.find((w) => {
      const wardNameEn = w.name_en?.toLowerCase() || '';
      const wardFullNameEn = w.full_name_en?.toLowerCase() || '';
      const wardNameEnNormalized = wardFullNameEn
        .replace(/\s+(ward|commune)$/i, '')
        .trim();
      const wardNameVi = w.name?.toLowerCase() || '';
      const wardFullNameVi = w.full_name?.toLowerCase() || '';
      const wardNameViNormalized = wardFullNameVi
        .replace(/\s+(phường|xã)$/i, '')
        .trim();

      return (
        (wardNameEn && lowerNormalizedName.includes(wardNameEn)) ||
        (wardNameEnNormalized &&
          lowerNormalizedName.includes(wardNameEnNormalized)) ||
        (wardNameEn?.includes(lowerNormalizedName)) ||
        (wardFullNameEn?.includes(lowerNormalizedName)) ||
        (wardNameVi && lowerNormalizedName.includes(wardNameVi)) ||
        (wardNameViNormalized &&
          lowerNormalizedName.includes(wardNameViNormalized)) ||
        (wardNameVi?.includes(lowerNormalizedName)) ||
        (wardFullNameVi?.includes(lowerNormalizedName))
      );
    });
  }
}
